import React, { useState, useContext, useEffect } from "react";

import Divider from "../../shared/components/divider/divider";
import SecondaryNav from "../../shared/components/secondary-nav/secondary-nav";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { Media } from "../../shared/contexts/media/MediaContext";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useColumnEnd from "../../shared/hooks/column-end/useColumnEnd";
import useFeatureToggle from "../../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, GlobalNavContent, IconPack, SecondaryNavContent } from "../../shared/models/app-data-model";
import "../shared/navigation-shared.scss";
import "./global-nav-mobile.scss";

export type GlobalNavMobileProps = {
  content: GlobalNavContent;
  iconPack: IconPack[];
  featureToggles: FeatureToggle[];
};

const GlobalNavMobile: React.FC<GlobalNavMobileProps> = ({ content, iconPack, featureToggles }: GlobalNavMobileProps) => {
  const auxNavEnabled = useFeatureToggle("aux-nav", featureToggles);
  const showMyChartLink = useFeatureToggle("mychart-link", featureToggles);
  const [selectedSecondaryNavItem, setSelectedSecondaryNavItem] = useState([] as SecondaryNavContent[]);
  const [columns, setContent] = useColumnEnd(selectedSecondaryNavItem || []);

  // This hook is responsible for updating our column-end hook whenever our content changes.
  useEffect(() => {
    setContent(selectedSecondaryNavItem || []);
  }, [selectedSecondaryNavItem, setContent]);

  const uiStateContext = useContext(UiStateContext);

  const primaryNavView = (
    <ul className="primary-nav">
      {content?.primary_nav?.map((item, index) => {
        const navItemWithChildren = item.primary_nav_item?.reference.find((el) => el.primary_nav);
        const navItemWithLink = item.primary_nav_item?.reference.find((el) => el.url || el.link);

        if (navItemWithChildren) {
          return (
            <li key={index}>
              {navItemWithChildren.primary_nav && (
                <button
                  className="primary-nav-item unstyled-btn"
                  data-testid="primary-nav-item-test"
                  onClick={() => {
                    // eslint-disable-next-line no-unused-expressions
                    uiStateContext.setBackButtonNavItem?.(true);
                    setSelectedSecondaryNavItem(navItemWithChildren.primary_nav ? navItemWithChildren.primary_nav : []);
                  }}
                >
                  {item.primary_nav_item.title}
                  <ShIcon iconPack={iconPack} className="icon" iconName="caret_right" />
                </button>
              )}
            </li>
          );
        } else if (navItemWithLink) {
          return (
            <li key={index}>
              <div data-testid="primary-nav-item">
                <ShLink
                  className="primary-nav-item"
                  reference={item.primary_nav_item.reference}
                  title={item.primary_nav_item.title}
                  featureToggles={featureToggles}
                />
              </div>
            </li>
          );
        }
      })}
    </ul>
  );

  const auxNavView = (
    <ul className="aux-nav" data-testid="mobile-aux-nav">
      {content?.auxiliary_nav?.map((item, index) => {
        const secondaryNavContent = item.primary_nav_item?.reference.find((el) => el.primary_nav);
        if (item.primary_nav_item) {
          return (
            <li key={index}>
              {item.primary_nav_item?.title && (
                <button
                  className="aux-item unstyled-btn"
                  data-testid="aux-item-test"
                  onClick={() => {
                    // eslint-disable-next-line no-unused-expressions
                    uiStateContext.setBackButtonNavItem?.(true);
                    setSelectedSecondaryNavItem(secondaryNavContent?.primary_nav ? secondaryNavContent.primary_nav : []);
                  }}
                >
                  {item.primary_nav_item.title}
                  <ShIcon iconPack={iconPack} className="icon" iconName="caret_right" />
                </button>
              )}
            </li>
          );
        }
      })}
      {showMyChartLink && (
        <li>
          <a
            title="MyChart Login"
            data-testid="mychart-login-link"
            href="https://mychart.spectrumhealth.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            MyChart Login
          </a>
        </li>
      )}
    </ul>
  );

  return (
    <Media lessThan="desktop">
      <div id="global-nav-mobile" className="global-nav-mobile" data-testid={uiStateContext.backButtonNavItem ? "backButtonNavItem" : ""}>
        {uiStateContext.backButtonNavItem
          ? selectedSecondaryNavItem && <SecondaryNav columns={columns} isOutsideClickHandlerEnabled={true} />
          : primaryNavView}
        {!uiStateContext.backButtonNavItem && content?.help && auxNavEnabled && (
          <>
            <Divider />
            <span>{auxNavView}</span>
            <Divider />
            <div data-testid="help-button">
              <ShLink reference={content.help?.reference} className="secondary-item-link" title={content?.help?.title} />
            </div>
          </>
        )}
      </div>
    </Media>
  );
};

export default GlobalNavMobile;
